import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../Header/Header";
// import Footer from "../FooterMain/Footer";
import { SearchContext } from "../../context/SearchContext";
import { MdTimeline } from "react-icons/md";
import NoResults from "./NoResults";
import { BiSolidArrowFromBottom } from "react-icons/bi";

const fuzzyMatch = (query, text) => {
  if (!query || !text) return false;
  const words = query.trim().split(/\s+/);
  const regex = new RegExp(words.map((word) => `(${word})`).join("|"), "i");
  return regex.test(text);
};

const highlightText = (text, query) => {
  if (!text) return "";
  if (!query) return text;
  const regex = new RegExp(`(${query})`, "gi");
  return text.split(regex).map((part, index) =>
    regex.test(part) ? (
      <span key={index} className="bg-yellow-200 font-semibold">
        {part}
      </span>
    ) : (
      part
    )
  );
};

const GlobalSearch = () => {
  const { setQuery, query } = useContext(SearchContext);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [mediumFilter, setMediumFilter] = useState("");
  const [ratingFilter, setRatingFilter] = useState(null);
  const [editionFilter, setEditionFilter] = useState(null);
  const [trendingSearches, setTrendingSearches] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTrendingSearches = async () => {
      try {
        const response = await fetch(
          `https://auth.ssccglpinnacle.com/api/trending-searches`
        );
        const data = await response.json();
        setTrendingSearches(data.map((item) => item.query));
      } catch (error) {
        console.error("Error fetching trending searches:", error);
      }
    };
    fetchTrendingSearches();
  }, []);

  useEffect(() => {
    const fetchResults = async () => {
      if (!query) return setResults([]);
      setLoading(true);
      try {
        const response = await fetch(
          `https://auth.ssccglpinnacle.com/api/search?query=${encodeURIComponent(
            query
          )}&category=${categoryFilter}&subject=${subjectFilter}&medium=${mediumFilter}&rating=${
            ratingFilter || ""
          }&edition=${editionFilter || ""}`
        );
        const data = await response.json();
        const filteredResults = data.filter((item) =>
          (item.title || item.courseTitle)
            .toLowerCase()
            .includes(query.toLowerCase())
        );
        setResults(filteredResults);
      } catch (error) {
        console.error("Error fetching search results:", error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [
    query,
    categoryFilter,
    subjectFilter,
    mediumFilter,
    ratingFilter,
    editionFilter,
  ]);

  const handleFilterChange = (type, value) => {
    if (type === "category") setCategoryFilter(value);
    if (type === "subject") setSubjectFilter(value);
    if (type === "medium") setMediumFilter(value);
    if (type === "rating") setRatingFilter(value);
    if (type === "edition") setEditionFilter(value);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigateToDetails = (type, id, permalink, SEOPermalink, slug) => {
    if (type === "book")
      window.open(`https://books.ssccglpinnacle.com${permalink}`, "_blank");
    else if (type === "video")
      window.open(
        `https://videos.ssccglpinnacle.com/${SEOPermalink}`,
        "_blank"
      );
    else if (type === "testpass")
      window.open(
        `https://testportal.ssccglpinnacle.com/buy/${slug}`,
        "_blank"
      );
    else if (type === "ebook")
      window.open(`https://ebooks.ssccglpinnacle.com${permalink}`, "_blank");
    else if (type === "typing")
      window.open(`https://typing.ssccglpinnacle.com/`, "_blank");
  };

  const tagColorClass = {
    testpass: "bg-blue-100 text-blue-600",
    book: "bg-green-100 text-green-600",
    video: "bg-red-100 text-red-600",
    ebook: "bg-purple-100 text-purple-600",
    typing: "bg-yellow-100 text-yellow-600",
  };

  const renderCard = (item) => {
    const tag =
      item.type === "testpass"
        ? "Test Pass"
        : item.type === "book"
        ? "Book"
        : item.type === "video"
        ? "Video"
        : item.type === "ebook"
        ? "E-book"
        : item.type === "typing"
        ? "Typing"
        : "";

    return (
      <div
        key={item._id}
        className="bg-white rounded shadow-md p-4 cursor-pointer hover:shadow-lg transition"
        onClick={() =>
          navigateToDetails(
            item.type,
            item._id,
            item.permalink,
            item.SEOPermalink,
            item.slug
          )
        }
      >
        <span
          className={`text-xs px-2 py-1 rounded ${
            tagColorClass[item.type]
          } inline-block mb-2`}
        >
          {tag}
        </span>
        <img
          src={item.image1 || item.image || item.englishCoverImage}
          alt={item.title || item.courseTitle}
          className="w-full h-40 object-contain mb-2"
        />
        <h4 className="text-base font-medium mb-2">
          {highlightText(item.title || item.courseTitle, query)}
        </h4>
        <button
          className="text-sm text-blue-600 underline hover:text-blue-800"
          onClick={(e) => {
            e.stopPropagation();
            navigateToDetails(
              item.type,
              item._id,
              item.permalink,
              item.SEOPermalink,
              item.slug
            );
          }}
        >
          Show More
        </button>
      </div>
    );
  };

  const orderedResults = [
    ...results.filter((item) => item.type === "testpass"),
    ...results.filter((item) => item.type === "book"),
    ...results.filter((item) => item.type === "video"),
    ...results.filter((item) => item.type === "ebook"),
    ...results.filter((item) => item.type === "typing"),
  ];

  return (
    <>
      <Header />
      <div className="flex flex-col md:flex-row max-w-7xl mx-auto px-4 py-6 gap-6">
        <aside className="w-full md:w-64 border-r pr-4">
          <h3 className="text-lg font-semibold mb-2">Trending Searches</h3>
          <ul className="space-y-2 mb-6">
            {trendingSearches.map((search, index) => (
              <li
                key={index}
                className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-blue-600"
                onClick={() => setQuery(search)}
              >
                <MdTimeline className="text-gray-500" />
                {search}
              </li>
            ))}
          </ul>

          <h3 className="text-lg font-semibold mb-2">Category</h3>
          <ul className="space-y-2">
            {["all", "testpass", "book", "video", "ebook", "typing"].map(
              (type) => (
                <li key={type} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="category"
                    checked={categoryFilter === type}
                    onChange={() => handleFilterChange("category", type)}
                  />
                  <label className="capitalize">
                    {type === "all" ? "All" : type}
                  </label>
                </li>
              )
            )}
          </ul>
        </aside>

        <main className="flex-1">
          {loading ? (
            <p className="text-center text-gray-500">Loading...</p>
          ) : (
            <>
              <h4 className="text-md font-medium mb-4">
                {orderedResults.length > 0
                  ? `We found ${orderedResults.length} result${
                      orderedResults.length > 1 ? "s" : ""
                    } for "${query}".`
                  : `No results found for "${query}".`}
              </h4>

              {orderedResults.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {orderedResults.map((item) => renderCard(item))}
                </div>
              ) : (
                <NoResults query={query} />
              )}
            </>
          )}
        </main>
      </div>

      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={scrollToTop}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg"
        >
          <BiSolidArrowFromBottom size={20} />
        </button>
      </div>
      {/* <Footer /> */}
    </>
  );
};

export default GlobalSearch;
